import { useEffect, useRef } from 'react';

interface UseParallaxOptions {
  speed?: number;
  direction?: 'up' | 'down';
  /** CSS selector for the child element to apply transform to.
   *  If not specified, applies to `[data-parallax-target]` or first child. */
  targetSelector?: string;
}

/**
 * GPU-accelerated parallax via direct DOM manipulation.
 * Zero React re-renders — transforms are applied directly to the target element.
 */
export const useParallax = (options: UseParallaxOptions = {}) => {
  const { speed = 0.3, direction = 'up', targetSelector } = options;
  const ref = useRef<HTMLDivElement>(null);
  const rafId = useRef<number>(0);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const target = (
      targetSelector
        ? container.querySelector<HTMLElement>(targetSelector)
        : container.querySelector<HTMLElement>('[data-parallax-target]')
          ?? container.firstElementChild as HTMLElement | null
    );

    if (!target) return;

    const handleScroll = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);

      rafId.current = requestAnimationFrame(() => {
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (rect.bottom < 0 || rect.top > windowHeight) return;

        const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
        const parallaxOffset = (scrollProgress - 0.5) * 100 * speed;
        const finalOffset = direction === 'up' ? -parallaxOffset : parallaxOffset;

        target.style.transform = `translateY(${finalOffset}px) scale(1.1)`;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [speed, direction, targetSelector]);

  return { ref };
};
